/**
 * Starter / Growth billing UI — Loop P2 frontend.
 *
 * Feature-flag gated via `GET /api/billing/sg/plans` (which returns 503 when
 * `FEATURE_BILLING_STARTER_GROWTH=false`). The parent Billing.js switches to
 * this component only when the flag is on. When rendered, it drives the
 * merchant through:
 *
 *   1. If no subscription   → plan picker + province picker → Stripe Checkout
 *   2. If active            → manage panel (cancel · reactivate · change plan
 *                             · manage provinces · invoices list)
 *
 * All monetary values come from the backend as strings and are passed to the
 * DOM as strings — we never do arithmetic client-side.
 */
import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import {
  Check, CreditCard, MapPin, Sparkles, Rocket, ArrowRight,
  RefreshCw, CalendarClock, ExternalLink, Loader2, AlertTriangle,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { PreLaunchBanner, ReservedPanel, CompleteReservationPanel } from "@/pages/merchant/PreLaunchPanels";
import { PlanSummaryCard } from "@/components/merchant/billing/PlanSummaryCard";
import { ChangePlanCard } from "@/components/merchant/billing/ChangePlanCard";
import { FeeCalculatorCard } from "@/components/merchant/billing/FeeCalculatorCard";

const CADENCES = [
  { id: "monthly", label: "Billed monthly" },
  { id: "annual",  label: "Billed annually" },
];

function cad(v) { return `CA$${Number(v).toFixed(2)}`; }

function StatusBadge({ status, cancelAtPeriodEnd }) {
  const map = {
    active:    { color: "emerald", label: cancelAtPeriodEnd ? "Cancelling" : "Active" },
    past_due:  { color: "amber",   label: "Payment failed — retrying" },
    unpaid:    { color: "red",     label: "Payment overdue" },
    canceled:  { color: "slate",   label: "Cancelled" },
    trialing:  { color: "cyan",    label: "Trial" },
  };
  const m = map[status] || { color: "slate", label: status || "Unknown" };
  const cls = {
    emerald: "bg-emerald-50 text-emerald-800 border-emerald-200",
    amber:   "bg-amber-50 text-amber-800 border-amber-200",
    red:     "bg-red-50 text-red-800 border-red-200",
    cyan:    "bg-cyan-50 text-cyan-800 border-cyan-200",
    slate:   "bg-slate-50 text-slate-700 border-slate-200",
  }[m.color];
  return (
    <span data-testid="sg-status-badge"
          className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full border text-xs font-semibold ${cls}`}>
      <span className="w-1.5 h-1.5 rounded-full bg-current opacity-60" />
      {m.label}
    </span>
  );
}

function PlanPrice({ plan, cadence, final }) {
  if (final && cadence === "annual") {
    return (
      <>
        <div className="mt-2 flex items-baseline gap-1">
          <span data-testid={`sg-plan-${plan.id}-price`} className="text-3xl font-bold text-slate-900">{cad(plan.annual_cad)}</span>
          <span className="text-sm text-slate-500">/yr</span>
        </div>
        <div className="text-xs text-emerald-700 font-semibold mt-1" data-testid={`sg-plan-${plan.id}-annual-note`}>
          {plan.annual_benefit || "No subscription fee"}
        </div>
      </>
    );
  }
  const price = cadence === "monthly" || final ? plan.monthly_cad : plan.annual_per_month_cad;
  return (
    <>
      <div className="mt-2 flex items-baseline gap-1">
        <span data-testid={`sg-plan-${plan.id}-price`} className="text-3xl font-bold text-slate-900">{cad(price)}</span>
        <span className="text-sm text-slate-500">/mo</span>
      </div>
      {cadence === "annual" && (
        <div className="text-xs text-slate-500 mt-1">
          Billed {cad(plan.annual_upfront_cad)} upfront
        </div>
      )}
    </>
  );
}

function PlanCard({ plan, cadence, provinces, onSubscribe, submitting, preLaunch, reserved, final, chosen }) {
  const isGrowth = plan.id === "growth";
  return (
    <div data-testid={`sg-plan-${plan.id}`}
         className={`relative rounded-2xl border p-6 flex flex-col ${
           isGrowth ? "border-cyan-400 bg-white" : "border-slate-200 bg-white/70"
         } ${chosen ? "ring-2 ring-emerald-400" : ""} backdrop-blur`}>
      {isGrowth && (
        <div className="absolute -top-3 left-6 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest bg-cyan-500 text-white">
          Recommended
        </div>
      )}
      <div className="flex items-center gap-2 mb-2">
        {isGrowth ? <Rocket className="w-5 h-5 text-cyan-600" /> : <Sparkles className="w-5 h-5 text-slate-600" />}
        <div className="font-display text-xl text-slate-900">{plan.name}</div>
      </div>
      <PlanPrice plan={plan} cadence={cadence} final={final} />
      <div className="mt-3 text-xs text-slate-600">
        Fee: <span className="font-semibold">{plan.fee_pct}%</span> or{" "}
        {cad(plan.fee_minimum_cad)}, whichever is higher · per verified sale
      </div>
      <div className="mt-2 text-xs text-slate-600">
        Included: <span className="font-semibold">
          {plan.storefronts_included || 1} storefront{(plan.storefronts_included || 1) > 1 ? "s" : ""}
        </span>{" · "}
        <span className="font-semibold">
          {plan.provinces_included_per_storefront || plan.provinces_included || 1} province{(plan.provinces_included_per_storefront || 1) > 1 ? "s" : ""}
        </span>{" per storefront"}
      </div>
      <ul className="mt-4 space-y-2 flex-1">
        {plan.features.map((f) => (
          <li key={f} className="flex items-start gap-2 text-sm text-slate-700">
            <Check className="w-4 h-4 text-cyan-600 mt-0.5 shrink-0" />
            <span>{f}</span>
          </li>
        ))}
      </ul>
      <button data-testid={`sg-plan-${plan.id}-cta`}
              disabled={submitting || provinces.length === 0}
              onClick={() => onSubscribe(plan.id)}
              className="mt-6 pf-btn-primary w-full py-3 rounded-full text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed">
        {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
        {preLaunch ? (reserved ? "Keep this plan" : "Subscribe") : `Start with ${plan.name}`}
      </button>
      {final && (
        <div className="mt-2 text-[11px] text-slate-500 text-center" data-testid={`sg-plan-${plan.id}-card-note`}>
          Card required{plan.monthly_cad === "0.00" ? " · no monthly fee, used for performance fees + add-ons" : ""}
        </div>
      )}
    </div>
  );
}

function ProvincePicker({ valid, provinces, setProvinces, max, final, provinceMonthly }) {
  const toggle = (code) => {
    if (provinces.includes(code)) {
      setProvinces(provinces.filter((p) => p !== code));
    } else if (!max || provinces.length < max) {
      setProvinces([...provinces, code]);
    }
  };
  return (
    <div data-testid="sg-province-picker" className="rounded-2xl border border-slate-200 bg-white/70 backdrop-blur p-5">
      <div className="flex items-center gap-2 mb-3">
        <MapPin className="w-4 h-4 text-slate-500" />
        <div className="text-sm font-semibold text-slate-900">Coverage — Canadian provinces</div>
      </div>
      <p className="text-xs text-slate-500 mb-3">
        Pick the provinces where you want Cyannect to promote your offers.
        {final ? " Basic includes 1 province per storefront, Starter 2 and Pro up to 4 " : " Starter includes 1 province per storefront, Growth includes up to 3 "}
        per storefront · extras are billed at {cad(provinceMonthly)}/mo each (15% off annual).
        Fine-grained per-storefront coverage lives under <a href="/merchant/storefronts" className="underline">Storefronts</a>.
        See the <a href="/merchant/faq#annual-addons" className="underline">merchant FAQ</a> for full pricing details.
      </p>
      <div className="flex flex-wrap gap-2">
        {valid.map((code) => {
          const selected = provinces.includes(code);
          return (
            <button key={code} data-testid={`sg-province-${code}`} onClick={() => toggle(code)}
                    className={`px-3 py-1.5 rounded-full border text-xs font-semibold transition ${
                      selected
                        ? "bg-cyan-600 text-white border-cyan-600"
                        : "bg-white text-slate-700 border-slate-300 hover:border-cyan-400"
                    }`}>
              {code}
            </button>
          );
        })}
      </div>
      <div className="text-xs text-slate-500 mt-3">
        Selected: {provinces.length === 0 ? "none" : provinces.join(", ")}
      </div>
    </div>
  );
}

function SubscribePanel({ catalog, storeStatus, preLaunch, initial, onReserved, onCancelEdit }) {
  const final = !!catalog.final_active;
  const chosen = new URLSearchParams(window.location.search).get("plan");
  const [cadence, setCadence] = useState(initial?.cadence || (final ? "monthly" : "annual"));
  const [provinces, setProvinces] = useState(initial?.provinces || []);
  const [submitting, setSubmitting] = useState(false);
  const isPending = storeStatus && storeStatus !== "approved";

  const subscribe = async (planId) => {
    try {
      setSubmitting(true);
      const { data } = await api.post("/billing/sg/subscribe", {
        plan: planId,
        cadence,
        provinces,
        origin_url: window.location.origin,
      });
      if (data?.status === "pre_launch_reserved") {
        toast.success("Plan reserved. No payment until launch.");
        onReserved?.();
        return;
      }
      if (data?.url) window.location.assign(data.url);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start checkout.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      {isPending && !preLaunch && (
        <div
          className="rounded-2xl border border-cyan-200 bg-cyan-50 p-4 text-sm text-cyan-900"
          data-testid="sg-verification-notice"
        >
          <div className="font-semibold">Subscribe now — verification still runs in parallel</div>
          <div className="mt-1 text-cyan-800">
            You can pick a plan and start billing right away. Cyannect KYB
            verification is still needed before any offer you draft can go
            live to shoppers — we'll ping you the moment it clears.
          </div>
        </div>
      )}

      <div className="flex items-center gap-2 justify-center">
        {CADENCES.map((c) => (
          <button key={c.id} data-testid={`sg-cadence-${c.id}`} onClick={() => setCadence(c.id)}
                  className={`px-4 py-2 rounded-full text-xs font-semibold border transition ${
                    cadence === c.id
                      ? "bg-slate-900 text-white border-slate-900"
                      : "bg-white text-slate-700 border-slate-300"
                  }`}>
            {c.label}
          </button>
        ))}
      </div>

      <ProvincePicker
        valid={catalog.valid_provinces}
        provinces={provinces}
        setProvinces={setProvinces}
        max={13}
        final={final}
        provinceMonthly={catalog.additional_province?.monthly_cad}
      />

      {preLaunch && <PreLaunchBanner />}

      <div className={`grid gap-5 ${catalog.plans.length === 3 ? "md:grid-cols-3" : "md:grid-cols-2"}`} data-testid="sg-plan-grid">
        {catalog.plans.map((plan) => (
          <PlanCard key={plan.id} plan={plan} cadence={cadence}
                    provinces={provinces} onSubscribe={subscribe}
                    submitting={submitting} preLaunch={preLaunch}
                    reserved={initial?.plan === plan.id}
                    final={final} chosen={chosen === plan.id} />
        ))}
      </div>

      {final && <FeeCalculatorCard />}

      {onCancelEdit && (
        <div className="text-center">
          <button data-testid="pre-launch-back-btn" onClick={onCancelEdit}
                  className="text-xs font-semibold text-slate-600 hover:text-slate-900 underline">
            Back to my reservation
          </button>
        </div>
      )}

      {provinces.length === 0 && (
        <p className="text-center text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg py-2 px-3">
          Pick at least one province to enable checkout.
        </p>
      )}
    </div>
  );
}

function ManagePanel({ status, catalog, onRefresh }) {
  const [invoices, setInvoices] = useState([]);
  const [settlements, setSettlements] = useState([]);
  const [pending, setPending] = useState(null);
  const [busy, setBusy] = useState(null);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const load = useCallback(async () => {
    try {
      const [{ data: inv }, { data: st }, { data: pn }] = await Promise.all([
        api.get("/billing/sg/invoices"),
        api.get("/billing/sg/settlements"),
        api.get("/billing/sg/pending"),
      ]);
      setInvoices(inv?.invoices || []);
      setSettlements(st?.settlements || []);
      setPending(pn || null);
    } catch (e) {
      // Invoice / settlement history is a nice-to-have; the plan/status
      // panel above is the source of truth. Log the transient failure and
      // render an empty list so the merchant can still manage their sub.
      // eslint-disable-next-line no-console
      console.debug("[billing.sg] history load failed:", e?.message || e);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const call = async (label, fn) => {
    try {
      setBusy(label);
      await fn();
      toast.success("Done.");
      onRefresh();
      return true;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "That action failed.");
      return false;
    } finally {
      setBusy(null);
    }
  };

  const handleConfirmCancel = async () => {
    const ok = await call("cancel", () => api.post("/billing/sg/cancel"));
    if (ok) {
      setShowCancelDialog(false);
    }
  };

  const plan = catalog.plans.find((p) => p.id === status.plan);
  const final = !!catalog.final_active;
  const [summaryKey, setSummaryKey] = useState(0);
  const refreshAll = () => { setSummaryKey((k) => k + 1); onRefresh(); };
  const nameOf = (id) => catalog.plans.find((p) => p.id === id)?.name || id;
  const periodEnd = status.current_period_end
    ? new Date(status.current_period_end).toLocaleDateString("en-CA", { year: "numeric", month: "short", day: "numeric" })
    : null;
  const renewal = periodEnd
    ? <>{status.cancel_at_period_end ? "Ends" : "Renews"} on <strong>{periodEnd}</strong></>
    : status.test_bypass ? "Test subscription — no renewal date" : null;

  return (
    <div className="space-y-6" data-testid="sg-manage-panel">
      {/* Header card */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Current plan</div>
            <div className="mt-1 flex items-center gap-3">
              <div className="font-display text-2xl text-slate-900" data-testid="sg-current-plan">
                {plan ? plan.name : status.plan || "—"}
              </div>
              <StatusBadge status={status.status} cancelAtPeriodEnd={status.cancel_at_period_end} />
            </div>
            <div className="text-sm text-slate-600 mt-2" data-testid="sg-billing-line">
              {status.cadence === "annual" ? "Annual billing" : "Monthly billing"}
              {renewal && <span className="ml-1" data-testid="sg-renewal">· {renewal}</span>}
            </div>
            {status.pending_plan_change && !final && (
              <div className="mt-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 inline-flex items-center gap-2">
                <CalendarClock className="w-3.5 h-3.5" />
                Pending change to <strong>{nameOf(status.pending_plan_change.to_plan)}</strong>
                {" "}({status.pending_plan_change.to_cadence}) — takes effect at period end.
              </div>
            )}
          </div>
          <div className="flex gap-2">
            {status.cancel_at_period_end ? (
              <button data-testid="sg-reactivate-btn" disabled={busy}
                      onClick={() => call("react", () => api.post("/billing/sg/reactivate"))}
                      className="pf-btn-secondary px-4 py-2 rounded-full text-xs font-semibold">
                {busy === "react" ? <Loader2 className="w-4 h-4 animate-spin" /> : "Resume subscription"}
              </button>
            ) : (
              <button data-testid="sg-cancel-btn" disabled={busy}
                      onClick={() => setShowCancelDialog(true)}
                      className="text-red-700 hover:text-red-900 text-xs font-semibold underline">
                {busy === "cancel" ? "…" : "Cancel subscription"}
              </button>
            )}
          </div>
        </div>
      </div>

      {final && <PlanSummaryCard catalog={catalog} refreshKey={summaryKey} onChanged={onRefresh} />}
      {final && !status.cancel_at_period_end && (
        <ChangePlanCard catalog={catalog} status={status} currentPlan={status.effective_plan || status.plan || "basic"}
                        onChanged={refreshAll} />
      )}
      {final && <FeeCalculatorCard />}

      {/* Upgrade to Growth plan option for Starter subscribers */}
      {!final && status.plan === "starter" && catalog?.plans?.find((p) => p.id === "growth") && (
        <div className="rounded-2xl border-2 border-cyan-400 bg-gradient-to-br from-cyan-50/70 via-white to-sky-50/50 p-6 relative overflow-hidden" data-testid="sg-upgrade-growth-card">
          {(() => {
            const growthPlan = catalog.plans.find((p) => p.id === "growth");
            return (
              <>
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div>
                    <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest bg-cyan-600 text-white mb-2">
                      <Sparkles className="w-3 h-3" /> Upgrade to Growth
                    </div>
                    <div className="font-display text-2xl text-slate-900 flex items-center gap-2">
                      <Rocket className="w-6 h-6 text-cyan-600" /> Growth Plan
                    </div>
                    <p className="text-sm text-slate-600 mt-1 max-w-xl">
                      Scale your store with <strong>3 storefronts × 3 provinces each</strong>, lower performance fee (<strong>{growthPlan.fee_pct}%</strong> vs {plan?.fee_pct || 4.25}%), and unlimited active campaigns.
                    </p>
                  </div>
                  <div className="text-right">
                    <div className="text-3xl font-bold text-slate-900">
                      {cad(status.cadence === "annual" ? growthPlan.annual_per_month_cad : growthPlan.monthly_cad)}
                      <span className="text-sm font-normal text-slate-500">/mo</span>
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      {status.cadence === "annual" ? `Billed ${cad(growthPlan.annual_upfront_cad)} annually` : "Billed monthly"}
                    </div>
                  </div>
                </div>
                <div className="mt-5 flex items-center justify-between gap-4 flex-wrap pt-4 border-t border-cyan-100">
                  <div className="flex items-center gap-4 text-xs text-slate-700">
                    <span className="inline-flex items-center gap-1"><Check className="w-4 h-4 text-cyan-600" /> 3 Storefronts included</span>
                    <span className="inline-flex items-center gap-1"><Check className="w-4 h-4 text-cyan-600" /> 3 Provinces per storefront</span>
                    <span className="inline-flex items-center gap-1"><Check className="w-4 h-4 text-cyan-600" /> Lower {growthPlan.fee_pct}% fee</span>
                  </div>
                  <button
                    disabled={busy}
                    onClick={() => call("upgrade-growth", () => api.post("/billing/sg/change-plan", { plan: "growth", cadence: status.cadence || "monthly" }))}
                    className="pf-btn-primary px-6 py-2.5 rounded-full text-xs font-semibold inline-flex items-center gap-2"
                    data-testid="sg-upgrade-growth-btn"
                  >
                    {busy === "upgrade-growth" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Rocket className="w-4 h-4" />}
                    Upgrade to Growth Plan
                  </button>
                </div>
              </>
            );
          })()}
        </div>
      )}

      {/* Cancel Confirmation Popup */}
      <Dialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <DialogContent className="max-w-md" data-testid="sg-cancel-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-slate-900">
              <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0" />
              Cancel subscription?
            </DialogTitle>
            <DialogDescription className="text-sm text-slate-600 pt-1">
              Cancel at the end of the current billing period? You keep full access until then.
            </DialogDescription>
          </DialogHeader>

          <div className="rounded-xl bg-slate-50 border border-slate-200 p-4 text-xs text-slate-600 space-y-1.5 my-1">
            <div className="flex justify-between">
              <span className="text-slate-500">Current plan</span>
              <span className="font-semibold text-slate-900">{plan ? plan.name : status.plan || "—"}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Access continues until</span>
              <span className="font-semibold text-slate-900" data-testid="sg-cancel-access-until">{periodEnd || "the end of the current billing period"}</span>
            </div>
          </div>

          <DialogFooter className="gap-2 sm:gap-2 mt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowCancelDialog(false)}
              disabled={busy === "cancel"}
              data-testid="sg-cancel-dialog-keep"
              className="rounded-full text-xs font-semibold"
            >
              Keep subscription
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={handleConfirmCancel}
              disabled={busy === "cancel"}
              data-testid="sg-confirm-cancel-btn"
              className="bg-red-600 hover:bg-red-700 text-white rounded-full text-xs font-semibold inline-flex items-center gap-1.5"
            >
              {busy === "cancel" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
              {busy === "cancel" ? "Cancelling…" : "Confirm cancellation"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Provinces */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="sg-manage-provinces">
        <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-2">Coverage</div>
        <div className="flex flex-wrap gap-2 mb-3">
          {(status.provinces || []).map((p) => (
            <span key={p} className="px-3 py-1 rounded-full bg-slate-100 border border-slate-200 text-xs font-semibold">{p}</span>
          ))}
          {(status.provinces || []).length === 0 && <span className="text-xs text-slate-500">No provinces set</span>}
        </div>
        <div className="text-xs text-slate-600">
          Additional provinces beyond your plan: <strong>{status.additional_province_count}</strong>
          {status.additional_province_count > 0 && (
            <>{" "}·{" "}
              {status.cadence === "annual"
                ? `Billed ${cad(catalog.additional_province.annual_upfront_cad)} per province annually`
                : `Billed ${cad(catalog.additional_province.monthly_cad)} per province monthly`}
            </>
          )}
        </div>
        <a href="/pricing" className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-cyan-700 hover:underline">
          Change province coverage <ArrowRight className="w-3 h-3" />
        </a>
      </div>

      {/* Loop P3 — Performance Fee settlements */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="sg-settlements">
        <div className="flex items-center justify-between mb-3">
          <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">
            Performance Fees
          </div>
          <button onClick={load} className="text-xs text-slate-500 hover:text-slate-700 inline-flex items-center gap-1">
            <RefreshCw className="w-3 h-3" /> Refresh
          </button>
        </div>

        {/* Upcoming preview */}
        {pending && (
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div className="rounded-xl bg-slate-50 border border-slate-200 p-4">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">
                Ready for next invoice
              </div>
              <div className="mt-1 font-display text-2xl text-slate-900" data-testid="sg-finalized-amount">
                CA${pending.finalized_cad}
              </div>
              <div className="text-[11px] text-slate-500 mt-1">
                {pending.finalized_count} finalised sale{pending.finalized_count === 1 ? "" : "s"} ·
                bills {pending.next_run_at
                  ? new Date(pending.next_run_at).toLocaleDateString("en-CA", { month: "short", day: "numeric" })
                  : "next Mon"}
              </div>
            </div>
            <div className="rounded-xl bg-white border border-dashed border-slate-300 p-4">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">
                Still within {pending.finality_days}-day window
              </div>
              <div className="mt-1 font-display text-2xl text-slate-500" data-testid="sg-pending-amount">
                CA${pending.pending_cad}
              </div>
              <div className="text-[11px] text-slate-500 mt-1">
                {pending.pending_count} sale{pending.pending_count === 1 ? "" : "s"} ·
                held for refund/dispute
              </div>
            </div>
          </div>
        )}

        {/* History table */}
        {settlements.length === 0 ? (
          <div className="text-sm text-slate-500">
            No settlements yet. Your first weekly invoice will land on {" "}
            {pending?.next_run_at
              ? new Date(pending.next_run_at).toLocaleDateString("en-CA", { weekday: "long", month: "short", day: "numeric" })
              : "the following Monday"}.
          </div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {settlements.map((s) => (
              <li key={s.id} className="py-2.5 flex items-center justify-between text-sm">
                <div>
                  <div className="font-medium text-slate-800">
                    {s.period_id}
                    <span className={`ml-2 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full ${
                      s.invoice_status === "paid"    ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                        : s.invoice_status === "open" ? "bg-amber-50 text-amber-700 border border-amber-200"
                        : s.status === "failed"      ? "bg-red-50 text-red-700 border border-red-200"
                        : "bg-slate-50 text-slate-600 border border-slate-200"
                    }`}>{s.invoice_status || s.status}</span>
                  </div>
                  <div className="text-xs text-slate-500">
                    {s.conversion_count} sale{s.conversion_count === 1 ? "" : "s"} ·
                    {" "}{new Date(s.period_start).toLocaleDateString("en-CA", { month: "short", day: "numeric" })}
                    {" – "}
                    {new Date(new Date(s.period_end).getTime() - 86400000).toLocaleDateString("en-CA", { month: "short", day: "numeric" })}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-right">
                    <div className="font-semibold text-slate-900">
                      {cad(s.gross_fees)}
                    </div>
                    {s.status === "failed" && (
                      <div className="text-[10px] text-red-600 max-w-[220px] truncate">
                        {s.failure_reason || "Charge failed"}
                      </div>
                    )}
                  </div>
                  {s.hosted_invoice_url && (
                    <a href={s.hosted_invoice_url} target="_blank" rel="noopener noreferrer"
                       className="text-xs text-cyan-700 hover:underline inline-flex items-center gap-1"
                       data-testid={`sg-settlement-view-${s.id}`}>
                      View <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Invoices */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="sg-invoices">
        <div className="flex items-center justify-between mb-3">
          <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Invoices</div>
          <button onClick={load} className="text-xs text-slate-500 hover:text-slate-700 inline-flex items-center gap-1">
            <RefreshCw className="w-3 h-3" /> Refresh
          </button>
        </div>
        {invoices.length === 0 ? (
          <div className="text-sm text-slate-500">No invoices yet.</div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {invoices.map((inv) => (
              <li key={inv.id || inv.stripe_invoice_id} className="py-2.5 flex items-center justify-between text-sm">
                <div>
                  <div className="font-medium text-slate-800">
                    {inv.number || inv.stripe_invoice_id}
                    <span className={`ml-2 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full ${
                      inv.status === "paid" ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                        : inv.status === "open" ? "bg-amber-50 text-amber-700 border border-amber-200"
                        : "bg-slate-50 text-slate-600 border border-slate-200"
                    }`}>{inv.status}</span>
                  </div>
                  <div className="text-xs text-slate-500">
                    {inv.paid_at ? new Date(inv.paid_at).toLocaleDateString("en-CA") :
                     inv.created_at ? new Date(inv.created_at).toLocaleDateString("en-CA") : ""}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-right">
                    <div className="font-semibold text-slate-900">
                      {cad((inv.amount_paid_minor || inv.amount_due_minor || 0) / 100)}
                    </div>
                    {inv.tax_minor ? (
                      <div className="text-[10px] text-slate-500">tax {cad(inv.tax_minor / 100)}</div>
                    ) : null}
                  </div>
                  {inv.hosted_invoice_url && (
                    <a href={inv.hosted_invoice_url} target="_blank" rel="noopener noreferrer"
                       className="text-xs text-cyan-700 hover:underline inline-flex items-center gap-1">
                      View <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

export default function StarterGrowthBilling() {
  const [catalog, setCatalog] = useState(null);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(false);
  const [completing, setCompleting] = useState(false);

  const completeReservation = useCallback(async (r) => {
    try {
      setCompleting(true);
      const { data } = await api.post("/billing/sg/subscribe", {
        plan: r.plan, cadence: r.cadence, provinces: r.provinces, origin_url: window.location.origin,
      });
      if (data?.url) window.location.assign(data.url);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start checkout.");
    } finally {
      setCompleting(false);
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: cat }, { data: st }] = await Promise.all([
        api.get("/billing/sg/plans"),
        api.get("/billing/sg/status"),
      ]);
      setCatalog(cat);
      setStatus(st);
      setEditing(false);
      setError(null);
      return st;
    } catch (err) {
      setError(err?.response?.data?.detail || "Billing not available right now.");
    } finally {
      setLoading(false);
    }
  }, []);

  // Reconcile-on-return from Stripe. Stripe redirects to
  // `/merchant/billing?session_id={CHECKOUT_SESSION_ID}` on a successful
  // checkout — we call `/billing/sg/sync/{session_id}` to apply the
  // subscription immediately (webhooks don't reach preview / local dev),
  // then refresh the local state so ManagePanel renders + downstream
  // features (campaign creation, tier gating) unlock without a reload.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sid = params.get("session_id");
    if (!sid) {
      load().then((st) => {
        if (params.get("complete_reservation") && st && !st.pre_launch_mode && st.pre_launch_reserved) {
          window.history.replaceState({}, "", "/merchant/billing");
          completeReservation(st.pre_launch_reservation);
        }
      });
      return;
    }
    (async () => {
      try {
        setLoading(true);
        await api.post(`/billing/sg/sync/${encodeURIComponent(sid)}`);
        toast.success("Subscription activated.");
        // Clean the URL so a reload doesn't retry sync.
        window.history.replaceState({}, "", "/merchant/billing");
      } catch (err) {
        toast.error(
          err?.response?.data?.detail
          || "Payment received, but we couldn't confirm the subscription. Refresh to retry.",
        );
      } finally {
        await load();
      }
    })();
  }, [load, completeReservation]);

  if (loading) return (
    <div className="max-w-3xl mx-auto px-6 py-16 text-center text-slate-500">
      <Loader2 className="w-6 h-6 animate-spin mx-auto mb-3" /> Loading billing…
    </div>
  );
  if (error) return (
    <div className="max-w-2xl mx-auto px-6 py-12">
      <div className="rounded-2xl border border-amber-200 bg-amber-50 p-6 text-amber-900 text-sm">
        {error}
      </div>
    </div>
  );

  return (
    <div className="max-w-4xl mx-auto px-6 py-10" data-testid="starter-growth-billing">
      <div className="mb-6">
        <div className="text-xs uppercase tracking-widest text-cyan-700 font-semibold">Billing</div>
        <h1 className="font-display text-3xl text-slate-900">Cyannect merchant subscription</h1>
        <p className="text-sm text-slate-600 mt-1">
          {catalog?.final_active
            ? "Basic, Starter or Pro — change plans any time. All prices CAD; applicable taxes are added at checkout."
            : "Two simple plans. Cancel anytime. All prices CAD; applicable taxes are added at checkout."}
        </p>
      </div>

      {catalog?.final_active && !status?.has_subscription && (
        <div className="mb-6"><PlanSummaryCard catalog={catalog} /></div>
      )}
      {status?.has_subscription ? (
        <ManagePanel status={status} catalog={catalog} onRefresh={load} />
      ) : status?.pre_launch_reserved && !status.pre_launch_mode ? (
        <CompleteReservationPanel status={status} catalog={catalog} submitting={completing}
                                  onComplete={() => completeReservation(status.pre_launch_reservation)} />
      ) : status?.pre_launch_reserved && !editing ? (
        <ReservedPanel status={status} catalog={catalog} onChange={() => setEditing(true)} onRefresh={load} />
      ) : (
        <SubscribePanel catalog={catalog} storeStatus={status?.store_status}
                        preLaunch={!!status?.pre_launch_mode}
                        initial={editing ? status?.pre_launch_reservation : null}
                        onReserved={load}
                        onCancelEdit={editing ? () => setEditing(false) : null} />
      )}
    </div>
  );
}
